import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";

/**
 * Shared layout for /privacy /terms /guarantee /accessibility pages.
 *
 * Renders an editorial-quality long-read with a sticky left rail (table of
 * contents on lg+), prose-friendly typography, and the existing Navbar + Footer
 * so the site stays cohesive.
 *
 * Props:
 *   - eyebrow: small kicker label (e.g. "Legal")
 *   - title: page title
 *   - lede: subhead/lede paragraph
 *   - updated: "Last updated …" string
 *   - sections: [{ id, heading, body: ReactNode }]
 */
const LegalPage = ({ eyebrow = "Legal", title, lede, updated, sections }) => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    document.title = `${title} · MeinHaus`;
  }, [title]);

  return (
    <div className="relative min-h-screen bg-white flex flex-col">
      <Navbar />

      {/* Header */}
      <header className="relative pt-16 lg:pt-24 pb-12 lg:pb-16 border-b border-slate-200">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <Link
            to="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-2 text-[12px] font-bold tracking-[0.2em] uppercase text-[#1AA0DD] hover:text-[#0E7AAF] transition-colors mb-7"
          >
            <svg width="16" height="9" viewBox="0 0 16 9" fill="none" aria-hidden="true">
              <path
                d="M15 4.5 H1 M5 1 L1 4.5 L5 8"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back to MeinHaus
          </Link>

          <div className="inline-flex items-center gap-2 mb-5">
            <span className="block h-px w-8 bg-[#1AA0DD]" />
            <span className="text-[11px] sm:text-[12px] font-bold tracking-[0.24em] uppercase text-[#1AA0DD]">
              {eyebrow}
            </span>
          </div>

          <h1
            className="font-extrabold text-[#0f172a] tracking-tight text-[36px] sm:text-[48px] lg:text-[60px] leading-[1.02]"
            style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
          >
            {title}
          </h1>

          {lede && (
            <p className="mt-5 text-[#475569] text-[16px] sm:text-[18px] leading-[1.6] max-w-[720px]">
              {lede}
            </p>
          )}

          {updated && (
            <p className="mt-6 text-[12px] font-semibold tracking-[0.18em] uppercase text-slate-500">
              Last updated · {updated}
            </p>
          )}
        </div>
      </header>

      {/* Body */}
      <main className="flex-1 py-14 lg:py-20">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Sticky TOC */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-28">
              <h2 className="text-[10px] font-bold tracking-[0.22em] uppercase text-slate-500 mb-4">
                On this page
              </h2>
              <ol className="space-y-2.5 border-l border-slate-200 pl-4">
                {sections.map((s, i) => (
                  <li key={s.id} className="flex gap-3">
                    <span className="text-[11px] font-bold tracking-[0.16em] text-[#1AA0DD] tabular-nums pt-[2px]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <a
                      href={`#${s.id}`}
                      className="text-[13.5px] font-semibold text-[#0f172a] hover:text-[#1AA0DD] transition-colors leading-snug"
                    >
                      {s.heading}
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          </aside>

          {/* Article */}
          <article className="lg:col-span-9 max-w-[760px]">
            {sections.map((s, i) => (
              <section key={s.id} id={s.id} className="mb-12 lg:mb-14 scroll-mt-24">
                <div className="flex items-baseline gap-4 mb-4">
                  <span className="text-[12px] font-bold tracking-[0.18em] text-[#1AA0DD] tabular-nums">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h2
                    className="font-extrabold text-[#0f172a] tracking-tight text-[22px] sm:text-[26px] lg:text-[28px] leading-[1.15]"
                    style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
                  >
                    {s.heading}
                  </h2>
                </div>
                <div className="legal-prose pl-10 text-[#334155] text-[15px] sm:text-[15.5px] leading-[1.75] space-y-4">
                  {s.body}
                </div>
              </section>
            ))}

            {/* Footer note */}
            <div className="pl-10 mt-12 pt-8 border-t border-slate-200">
              <p className="text-[13.5px] text-slate-500 leading-[1.7]">
                Questions about this document?{" "}
                <a
                  href="mailto:legal@meinhaus.ca"
                  className="text-[#1AA0DD] hover:text-[#0E7AAF] font-semibold transition-colors"
                >
                  legal@meinhaus.ca
                </a>
              </p>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LegalPage;
